/**
 * Upload of one client document via a presigned URL (docs/API.md, "Документи"):
 *   1. POST /cabinet/cases/{id}/documents/upload-url {document_type_key, file_name, mime, size}
 *   2. PUT <upload_url> with the returned headers (Content-Type) — straight to object storage
 *   3. POST /cabinet/documents/{document_id}/complete
 *
 * Retrying after a failed PUT starts again from step 1 (the presigned URL may have expired);
 * retrying after a failed "complete" only repeats step 3.
 */
import { ApiError, isApiError, NETWORK_ERROR } from '../../api/client';
import type { UploadUrlResponse } from '../../api/types';
import { ACCEPTED_MIME } from '../../config';

/** Ліміт, якщо GET /public/site не віддав max_upload_bytes (старий API або сайт ще не завантажився). */
export const FALLBACK_MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

export type UploadPhase = 'preparing' | 'uploading' | 'finishing' | 'done' | 'error';
export type UploadErrorCode = 'too_large' | 'bad_type' | 'empty' | 'network' | 'rate_limited' | 'failed';

export class UploadError extends Error {
  constructor(readonly code: UploadErrorCode, readonly cause?: unknown) {
    super(code);
    this.name = 'UploadError';
  }
}

const EXT_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  heic: 'image/heic',
  heif: 'image/heif',
  webp: 'image/webp',
  pdf: 'application/pdf',
};

/** Some phones give HEIC photos an empty `type`; fall back to the extension. */
export function fileMime(file: { name: string; type: string }): string {
  if (file.type) return file.type.toLowerCase();
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return EXT_MIME[ext] ?? 'application/octet-stream';
}

/** maxBytes — з інформації про сайт (useSite().maxUploadBytes, GET /public/site). */
export function validateFile(file: { name: string; type: string; size: number }, maxBytes: number = FALLBACK_MAX_UPLOAD_BYTES): UploadErrorCode | null {
  if (file.size === 0) return 'empty';
  if (file.size > maxBytes) return 'too_large';
  const mime = fileMime(file);
  if (!ACCEPTED_MIME.includes(mime) && !mime.startsWith('image/')) return 'bad_type';
  return null;
}

export type ProgressFn = (fraction: number) => void;
export type PutFn = (url: string, file: Blob, headers: Record<string, string>, onProgress: ProgressFn, signal?: AbortSignal) => Promise<void>;

/** PUT with upload progress (browsers). */
export const xhrPut: PutFn = (url, file, headers, onProgress, signal) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable && ev.total > 0) onProgress(ev.loaded / ev.total);
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new ApiError(xhr.status, `storage_${xhr.status}`)));
    xhr.onerror = () => reject(new ApiError(0, NETWORK_ERROR));
    xhr.ontimeout = () => reject(new ApiError(0, NETWORK_ERROR));
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
    if (signal) {
      if (signal.aborted) return xhr.abort();
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(file);
  });

/** PUT via fetch (no byte-level progress). Used in tests and as a fallback. */
export const fetchPut: PutFn = async (url, file, headers, onProgress, signal) => {
  let res: Response;
  try {
    res = await fetch(url, { method: 'PUT', body: file, headers, signal });
  } catch (e) {
    if ((e as { name?: string })?.name === 'AbortError') throw e;
    throw new ApiError(0, NETWORK_ERROR);
  }
  if (!res.ok) throw new ApiError(res.status, `storage_${res.status}`);
  onProgress(1);
};

export const defaultPut: PutFn = typeof XMLHttpRequest !== 'undefined' ? xhrPut : fetchPut;

export interface UploadApi {
  uploadUrl: (
    caseId: string,
    body: { document_type_key?: string; checklist_item_id?: string; file_name: string; mime: string; size: number },
  ) => Promise<UploadUrlResponse>;
  completeUpload: (documentId: string) => Promise<unknown>;
}

export interface UploadParams {
  api: UploadApi;
  caseId: string;
  file: File;
  /** Ліміт розміру з GET /public/site (max_upload_bytes); без нього — 20 МБ. */
  maxBytes?: number;
  documentTypeKey?: string;
  checklistItemId?: string;
  put?: PutFn;
  signal?: AbortSignal;
  onPhase?: (p: UploadPhase) => void;
  onProgress?: ProgressFn;
  /** Set when the file is already in storage and only "complete" failed. */
  resumeDocumentId?: string;
  /** Called after the PUT succeeds, so a retry can resume at "complete". */
  onStored?: (documentId: string) => void;
}

export function toUploadError(e: unknown): UploadError {
  if (e instanceof UploadError) return e;
  if (isApiError(e)) {
    if (e.code === NETWORK_ERROR) return new UploadError('network', e);
    if (e.status === 429) return new UploadError('rate_limited', e);
    if (e.status === 413 || e.code === 'invalid_size') return new UploadError('too_large', e);
    if (e.code === 'unsupported_type') return new UploadError('bad_type', e);
    if (e.status === 415) return new UploadError('bad_type', e);
  }
  return new UploadError('failed', e);
}

export async function uploadDocument(p: UploadParams): Promise<{ documentId: string }> {
  const put = p.put ?? defaultPut;
  const invalid = validateFile(p.file, p.maxBytes);
  if (invalid) throw new UploadError(invalid);
  const mime = fileMime(p.file);
  try {
    let documentId = p.resumeDocumentId;
    if (!documentId) {
      p.onPhase?.('preparing');
      const target = await p.api.uploadUrl(p.caseId, {
        ...(p.documentTypeKey ? { document_type_key: p.documentTypeKey } : {}),
        ...(p.checklistItemId ? { checklist_item_id: p.checklistItemId } : {}),
        file_name: p.file.name || 'document',
        mime,
        size: p.file.size,
      });
      if (!target?.upload_url || !target.document_id) throw new UploadError('failed');
      p.onPhase?.('uploading');
      p.onProgress?.(0);
      const headers = { 'Content-Type': mime, ...(target.headers ?? {}) };
      await put(target.upload_url, p.file, headers, (f) => p.onProgress?.(Math.min(1, Math.max(0, f))), p.signal);
      p.onProgress?.(1);
      documentId = target.document_id;
      p.onStored?.(documentId);
    }
    p.onPhase?.('finishing');
    await p.api.completeUpload(documentId);
    p.onPhase?.('done');
    return { documentId };
  } catch (e) {
    if ((e as { name?: string })?.name === 'AbortError') throw e;
    p.onPhase?.('error');
    throw toUploadError(e);
  }
}
